import React, { ReactElement, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  AsyncButton,
  Button,
  ButtonStyle,
  IOrganisationResponse,
  alertSuccess,
} from '@one-tree/library'
import Embed from '../../components/Embed'

import Card, { CardSize } from '../../components/page/Card'
import CardContent from '../../components/page/CardContent'
import CardHeader from '../../components/page/CardHeader'
import { useOrganisation } from '../../context/OrganisationProvider'
import { patchOrganisation } from '../../helpers/APIHelper'
import { voucherWord } from '../../helpers/FormatHelper'
import Page from '../../hoc/Page'
import { RoutePath } from '../../types/Routes'
import WebShopTheme from './WebShopTheme'
import WebShopDetails from './WebShopText'
import WebShopAnalytics from './WebShopAnalytics'
import AnnouncementBar from './AnnouncementBar'
import ItemPreview from './ItemPreview'
import { GridWithPreview } from './WebShopHelper'
import { useDraft } from '../../hooks/useDraft'
import { IOrganisationPatchBody } from '../../types/API'

function WebShop(): ReactElement {
  const history = useHistory()

  const { organisation, saveOrganisation } = useOrganisation()
  const { values, changes, handleChange, hasChanges, reset } = useDraft<
    IOrganisationResponse,
    IOrganisationPatchBody
  >(organisation ?? undefined)

  const [showEmbed, setShowEmbed] = useState(false)

  if (!organisation || !('id' in values)) return <div>No organisation found</div>

  const deliveryError = !values.emailDelivery && !values.postDelivery

  const handleSaveWebShop = async (): Promise<void> => {
    if (hasChanges) {
      const res = await patchOrganisation({
        organisationId: organisation.id,
        patchOptions: changes,
      })
      if (res) {
        saveOrganisation(res)
        reset()
        alertSuccess('Web shop saved')
      }
    }
  }

  return (
    <Page warnBeforeExit={Boolean(hasChanges)}>
      <Card cardSize={CardSize.Medium}>
        <CardHeader title="Customise your web shop">
          <Button
            buttonStyle={ButtonStyle.Secondary}
            onClick={(): void => history.push(RoutePath.Home)}
          >
            Cancel
          </Button>
          <AsyncButton
            buttonStyle={ButtonStyle.Secondary}
            onClick={(): void => setShowEmbed(true)}
            disabled={!organisation}
          >
            Embed
          </AsyncButton>
          <AsyncButton
            buttonStyle={ButtonStyle.Action}
            disabled={!hasChanges || deliveryError}
            onClick={handleSaveWebShop}
          >
            Save
          </AsyncButton>
        </CardHeader>
        <CardContent>
          {organisation && (
            <>
              <GridWithPreview>
                <div>
                  <WebShopTheme values={values} handleChange={handleChange} />
                  <AnnouncementBar
                    values={values}
                    handleChange={handleChange}
                  />
                </div>
                <ItemPreview values={values} />
              </GridWithPreview>
              <WebShopDetails
                values={values}
                handleChange={handleChange}
                deliveryError={deliveryError}
              />
              <WebShopAnalytics values={values} handleChange={handleChange} />
            </>
          )}
        </CardContent>
      </Card>
      <Embed
        modalOpen={showEmbed}
        setModalOpen={setShowEmbed}
        embedUrl={organisation?.embedUrl}
        customTitle={`Embed the ${voucherWord(
          organisation,
        )} shop on your website.`}
      />
    </Page>
  )
}
export default WebShop
